import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, RefreshCw, IndianRupee, ShoppingBag, Users, Repeat, PackageX, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, inr, dt } from './adminApi';

const Stat = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className="rounded-lg border border-[var(--si-line)] bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-sm font-semibold text-[var(--si-grey)]"><Icon className="w-4 h-4" style={{ color }} />{label}</div>
    <p className="mt-1 text-2xl font-extrabold text-[var(--si-black)]">{value}</p>
    {sub && <p className="text-xs text-[var(--si-grey)]">{sub}</p>}
  </div>
);

const STAGES = [
  ['pending_payment', 'Payment Pending', '#B45309'], ['confirmed', 'New · In Queue', '#1D4ED8'], ['packed', 'Packed', '#6D28D9'],
  ['shipped', 'On the Way', '#0369A1'], ['delivered', 'Delivered', '#15803D'], ['cancelled', 'Cancelled', '#4B5563'], ['refunded', 'Refunded', '#B91C1C'],
];

const StuckAlert = ({ stuck, onOpen, onFix }) => (
  <div className="rounded-lg border-2 border-[#DC2626] bg-[#FEF2F2] p-4" data-testid="stuck-payments-alert">
    <p className="flex items-center gap-2 font-extrabold text-[#B91C1C]"><AlertTriangle className="w-5 h-5" /> {stuck.length} paid order{stuck.length > 1 ? 's' : ''} stuck: money received on Razorpay but order not confirmed</p>
    <div className="mt-3 space-y-2">
      {stuck.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-3 bg-white rounded border border-[#FCA5A5] p-3 text-sm" data-testid={`stuck-${s.id}`}>
          <button onClick={() => onOpen(s.id)} className="font-mono font-bold text-[var(--si-blue)] underline">#{s.id.slice(0, 8).toUpperCase()}</button>
          <span>{s.customer_name} · {s.customer_phone}</span>
          <span className="font-bold">{inr(s.total)}</span>
          <span className="text-[var(--si-grey)]">{s.razorpay.payment_id} ({s.razorpay.status})</span>
          <button onClick={() => onFix(s.id)} className="ml-auto si-btn si-btn-buy !py-1.5 !px-3 text-xs" data-testid={`stuck-confirm-${s.id}`}>Confirm order now</button>
        </div>
      ))}
    </div>
  </div>
);

export const AdminDashboard = ({ onOpenOrder, onGoOrders }) => {
  const [d, setD] = useState(null);
  const [stuck, setStuck] = useState(null);
  const load = useCallback(async () => {
    try { setD(await api.dashboard()); } catch (e) { toast.error(e.message); return; }
    setStuck(null);
    api.stuck().then((r) => setStuck(r)).catch(() => setStuck({ checked: 0, stuck: [], error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const fix = async (id) => {
    try { const r = await api.verifyPayment(id); toast[r.confirmed ? 'success' : 'error'](r.message); load(); }
    catch (e) { toast.error(e.message); }
  };
  if (!d) return <p className="py-10 text-center" data-testid="dashboard-loading">Loading dashboard…</p>;
  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Business overview</h2>
        <button onClick={load} className="si-btn si-btn-outline !py-2 !px-3 text-sm" data-testid="dashboard-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      {stuck === null ? <p className="text-sm text-[var(--si-grey)]" data-testid="stuck-checking">Checking Razorpay for stuck payments…</p>
        : stuck.stuck.length > 0 ? <StuckAlert stuck={stuck.stuck} onOpen={onOpenOrder} onFix={fix} />
        : <p className="flex items-center gap-2 text-sm font-semibold text-[var(--si-green)]" data-testid="stuck-none"><CheckCircle2 className="w-4 h-4" /> No stuck payments ({stuck.checked} pending order{stuck.checked === 1 ? '' : 's'} checked on Razorpay in the last 7 days)</p>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={IndianRupee} color="#15803D" label="Revenue today" value={inr(d.today.revenue)} sub={`${d.today.orders} paid orders`} testid="stat-today" />
        <Stat icon={IndianRupee} color="#15803D" label="Revenue this month" value={inr(d.month.revenue)} sub={`${d.month.orders} paid orders`} testid="stat-month" />
        <Stat icon={ShoppingBag} color="#1D4ED8" label="All-time revenue" value={inr(d.all_time.revenue)} sub={`${d.all_time.orders} paid orders · AOV ${inr(d.avg_order_value)}`} testid="stat-all" />
        <Stat icon={Users} color="#7E22CE" label="Customers" value={d.customers} sub={`${d.repeat_customers} repeat customers`} testid="stat-customers" />
      </div>

      <div>
        <p className="text-sm font-bold mb-2">Orders by stage</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {STAGES.map(([k, l, c]) => (
            <button key={k} onClick={() => onGoOrders(k)} data-testid={`stage-${k}`}
              className="rounded-lg border border-[var(--si-line)] bg-white p-3 text-left hover:shadow-md transition-shadow" style={{ borderTop: `4px solid ${c}` }}>
              <p className="text-2xl font-extrabold" style={{ color: c }}>{d.status_counts[k] || 0}</p>
              <p className="text-xs font-semibold text-[var(--si-black)]">{l}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-[var(--si-line)] bg-white p-4" data-testid="top-products">
          <p className="font-bold mb-3 flex items-center gap-2"><Repeat className="w-4 h-4" /> Top-selling products</p>
          {d.top_products.length === 0 ? <p className="text-sm text-[var(--si-grey)]">No sales yet.</p> : d.top_products.map((t) => (
            <div key={t.slug} className="flex items-center gap-3 py-1.5 border-b border-[var(--si-line)] last:border-0 text-sm">
              <img src={t.image} alt="" className="w-9 h-11 object-cover rounded" />
              <span className="flex-1">{t.name}</span><span className="font-bold">{t.units} sold</span><span className="w-24 text-right">{inr(t.revenue)}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-[var(--si-line)] bg-white p-4" data-testid="stock-alerts">
          <p className="font-bold mb-3 flex items-center gap-2"><PackageX className="w-4 h-4 text-[#B91C1C]" /> Stock alerts</p>
          <p className="text-sm mb-2"><b>{d.products_total}</b> products · <b>{d.products_hidden}</b> hidden · <b className="text-[#B91C1C]">{d.out_of_stock.length}</b> out of stock · <b className="text-[#B45309]">{d.low_stock.length}</b> low stock (≤5)</p>
          {[...d.out_of_stock.map((p) => ({ ...p, oos: true })), ...d.low_stock].slice(0, 10).map((p) => (
            <div key={p.slug} className="flex items-center gap-3 py-1.5 text-sm border-b border-[var(--si-line)] last:border-0">
              <span className="flex-1">{p.name}</span>
              <span className={`text-xs font-bold ${p.oos ? 'text-[#B91C1C]' : 'text-[#B45309]'}`}>{p.oos ? 'OUT OF STOCK' : `${p.stock} left`}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-[var(--si-grey)]">Updated {dt(new Date().toISOString())}</p>
    </div>
  );
};

export default AdminDashboard;
